import { AlertTriangle, ShieldAlert, ShieldCheck } from "lucide-react";
import type { EvGuardrail, GuardrailFinding } from "../../types/agent-chat";

/**
 * What the pre-call guardrails decided about the message just sent.
 *
 * Shown for warnings as well as refusals, so the check reads as something the
 * assistant does rather than only as something that stops you. Findings arrive
 * as a kind and a character range; the value itself is never sent, so this
 * component names what was seen and, where it can, shows where in the message
 * it was, without ever re-displaying the identifier.
 */
export function GuardrailNotice({
  guardrail,
  message,
}: {
  guardrail: EvGuardrail;
  /** The text the user sent, used only to locate the highlight. */
  message?: string;
}) {
  const checks = guardrail.checks.filter((c) => c.findings.length > 0);
  if (checks.length === 0) return null;

  const blocked = !guardrail.passed;
  const findings = checks.flatMap((c) => c.findings);
  const notice = checks.find((c) => c.message)?.message ?? null;

  return (
    <div
      role={blocked ? "alert" : "status"}
      className={[
        "mb-4 rounded-lg border px-4 py-3 text-sm",
        blocked
          ? "border-brand-error/30 bg-brand-error/10 text-brand-error"
          : "border-amber-400/40 bg-amber-50 text-amber-900",
      ].join(" ")}
    >
      <div className="flex items-start gap-2">
        {blocked ? (
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {blocked
              ? "Message bloqué : renseignements personnels sensibles"
              : "Renseignements personnels détectés"}
          </p>
          {notice && <p className="mt-1 opacity-90">{notice}</p>}

          <ul className="mt-2 space-y-1">
            {findings.map((f) => (
              <li key={`${f.kind}-${f.start}`} className="flex items-baseline gap-2">
                <span
                  className={[
                    "inline-block rounded px-1.5 py-0.5 text-xs font-medium",
                    f.severity === "block"
                      ? "bg-brand-error/20"
                      : "bg-amber-200/60",
                  ].join(" ")}
                >
                  {f.severity === "block" ? "bloqué" : "avertissement"}
                </span>
                <span>
                  {f.label}
                  {message ? <Excerpt message={message} finding={f} /> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * A few words around the finding, with the value itself masked.
 *
 * The span comes from the server; the text comes from the message the user
 * still has in front of them. Nothing sensitive crosses the network to build
 * this, and the identifier is replaced by its own length in bullets so the
 * user can find it without it being written down again.
 */
function Excerpt({ message, finding }: { message: string; finding: GuardrailFinding }) {
  const before = message.slice(Math.max(0, finding.start - 24), finding.start);
  const after = message.slice(finding.end, finding.end + 24);
  const masked = "•".repeat(Math.min(finding.end - finding.start, 24));

  return (
    <span className="ml-1 font-mono text-xs opacity-75">
      {before.trimStart() && <>…{before.trimStart()}</>}
      <mark className="rounded bg-current/20 px-1 text-current">{masked}</mark>
      {after.trimEnd() && <>{after.trimEnd()}…</>}
    </span>
  );
}

/** The passing case, for a turn where the guardrails found nothing. */
export function GuardrailPassed() {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-brand-muted">
      <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
      Aucun renseignement personnel détecté
    </span>
  );
}
